/**
 * Where a frame shows in the workspace: on the wall, in the Pinned rows, or
 * off (Part 04 decision 15, Part 09 decision 3). The same rule as
 * `backend/apps/spaces/wall_membership.py`; its test replays the shared
 * cases fixture `lib/__tests__/fixtures/wall-membership-cases.json`.
 *
 * The rule, first match wins:
 *
 * 1. an off-wall type (chats, questions, retired workflow and agent surfaces) → off
 * 2. the project's Setup frame (`metadata.project_role === "setup"`), the
 *    jobs' settings store → off
 * 3. a singleton that is not its type's canonical (oldest) frame → off
 * 4. a canonical tool (task board, memory) → tools; tools are never archived
 * 5. archived → off
 * 6. pinned, while the rows have room (newest pins first) → tools
 * 7. otherwise → wall
 *
 * Pinned holds `PINNED_TILE_CAP` tiles: the `FIXED_TILES` (chats and the
 * quick note, which are not frames), the tools present, then the pins.
 * `metadata.project_role` is server-owned.
 * `metadata.hidden` is canvas presentation state and plays no part.
 */

export type WallPlacement = "wall" | "tools" | "off";

const PINNED_TILE_CAP = 12;

/** The Pinned tiles that are not frames: chats and the quick note. */
const FIXED_TILES = 2;

/** The Project's tools, in Pinned row order (`TOOL_FRAME_TYPES`, backend). */
const TOOL_FRAME_TYPES = ["taskboard", "memory"] as const;

const TOOL_TYPES: ReadonlySet<string> = new Set(TOOL_FRAME_TYPES);

/** Frames the chat and question machinery shows, and retired types; never cards. */
export const OFF_WALL_FRAME_TYPES = [
  "conversation",
  "question",
  "connection_prompt",
  "workflow",
  "agent_list",
  "agent_settings",
] as const;

const OFF_WALL_TYPES: ReadonlySet<string> = new Set(OFF_WALL_FRAME_TYPES);

/** A type the chat and question code reads, or a retired one; never a card. */
export function isOffWallFrameType(frameType: string): boolean {
  return OFF_WALL_TYPES.has(frameType);
}

const SINGLETON_FRAME_TYPES: ReadonlySet<string> = new Set([
  "taskboard",
  "agent_list",
  "memory",
  "github",
]);

/** The fields the rule reads. */
export interface WallMember {
  frameId: string;
  frameType: string;
  archivedAt?: string | null;
  metadata: Readonly<Record<string, unknown>>;
}

export interface WallContext {
  /** Singleton type → its oldest frame id. */
  canonicalSingletons: ReadonlyMap<string, string>;
  /** Newest first, already cut to `pinSlots`. */
  pinnedTools: readonly string[];
}

/** Whether the user pinned `member` (`metadata.pinned`). */
export function isFramePinned(member: WallMember): boolean {
  return member.metadata.pinned === true;
}

/**
 * Off the wall whatever else is true (rules 1 and 2): an off-wall type or
 * the project's Setup frame.
 */
export function isNeverOnWall(
  member: Pick<WallMember, "frameType" | "metadata">,
): boolean {
  return (
    isOffWallFrameType(member.frameType) ||
    member.metadata.project_role === "setup"
  );
}

export function wallPlacement(
  member: WallMember,
  ctx: WallContext,
): WallPlacement {
  const { frameId, frameType } = member;
  if (isNeverOnWall(member)) return "off";
  if (
    SINGLETON_FRAME_TYPES.has(frameType) &&
    ctx.canonicalSingletons.get(frameType) !== frameId
  ) {
    return "off";
  }
  if (TOOL_TYPES.has(frameType)) return "tools";
  if (member.archivedAt) return "off";
  if (ctx.pinnedTools.includes(frameId)) return "tools";
  return "wall";
}

/** The Pinned rows' room for pins: the tile cap less the fixed tiles and the tools present. */
export function pinSlots(
  canonicalSingletons: ReadonlyMap<string, string>,
): number {
  const tools = TOOL_FRAME_TYPES.filter((type) =>
    canonicalSingletons.has(type),
  ).length;
  return Math.max(PINNED_TILE_CAP - FIXED_TILES - tools, 0);
}

/**
 * The context from a project's frames in server order (`created_at`, then
 * id), as the frames store keeps them. On the client these are the loaded
 * frames: the Pinned page holds every tool and fitting pin, so the context
 * agrees with the server's.
 */
export function buildWallContext(
  oldestFirst: readonly WallMember[],
): WallContext {
  const canonicalSingletons = new Map<string, string>();
  for (const member of oldestFirst) {
    if (
      SINGLETON_FRAME_TYPES.has(member.frameType) &&
      !canonicalSingletons.has(member.frameType)
    ) {
      canonicalSingletons.set(member.frameType, member.frameId);
    }
  }
  const preliminary: WallContext = { canonicalSingletons, pinnedTools: [] };
  const pinnedTools = oldestFirst
    .filter(
      (member) =>
        isFramePinned(member) && wallPlacement(member, preliminary) === "wall",
    )
    .reverse()
    .slice(0, pinSlots(canonicalSingletons))
    .map((member) => member.frameId);
  return { canonicalSingletons, pinnedTools };
}

/** The Project's own tool: its canonical task board or memory. */
export function isProjectTool(member: WallMember, ctx: WallContext): boolean {
  return (
    TOOL_TYPES.has(member.frameType) &&
    ctx.canonicalSingletons.get(member.frameType) === member.frameId
  );
}

/**
 * The Pinned frames in order: the task board and memory (those the Project
 * has), then pins, newest first.
 */
export function wallToolsRow(ctx: WallContext): string[] {
  const tools = TOOL_FRAME_TYPES.flatMap((type) => {
    const frameId = ctx.canonicalSingletons.get(type);
    return frameId ? [frameId] : [];
  });
  return [...tools, ...ctx.pinnedTools];
}
